import React from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'

import s from './Picture.scss'


const getResizedPath = (src, xValue, size) => {
  if (typeof xValue === 'string') {
    return xValue
  }

  return src.replace(/(\.[a-z]+)$/, `@${size}x$1`)
}

const getSrcSet = (src, x2, x3) => {
  let srcSet = src

  if (x2) {
    const url = getResizedPath(src, x2, 2)

    srcSet += `, ${url} 2x`
  }
  if (x3) {
    const url = getResizedPath(src, x3, 3)

    srcSet += `, ${url} 3x`
  }

  return srcSet
}


const OldPictureComponent = React.forwardRef((props, ref) => {
  const {
    className, imgClassName, srcTag, src, srcSet, srcSetTag, fullWidth,
    fullHeight, alt, onError, itemProp, dataTestId,
  } = props

  const rootClassName = cx(s.picture, className, {
    [s.fullWidth]: fullWidth,
    [s.fullHeight]: fullHeight,
  })

  // TODO why do we need global .image?
  const newImgClassName = cx('image', 'pictureImage', s.image, imgClassName)

  return (
    <picture className={rootClassName} itemProp={itemProp}>
      <source {...srcSetTag} />
      <img
        className={newImgClassName}
        alt={alt}
        ref={ref}
        onError={onError}
        data-testid={dataTestId}
        {...srcTag}
        {...srcSetTag}
      />
    </picture>
  )
})

const NewPictureComponent = React.forwardRef((props, ref) => {
  const {
    className, srcTag, srcSetTag, fullWidth,
    fullHeight, alt, onError, itemProp, dataTestId,
  } = props

  // TODO why do we need global .image?
  const rootClassName = cx('image', 'pictureImage', s.image, className, {
    [s.fullWidth]: fullWidth,
    [s.fullHeight]: fullHeight,
  })

  return (
    <img
      className={rootClassName}
      alt={alt}
      ref={ref}
      onError={onError}
      itemProp={itemProp}
      data-testid={dataTestId}
      {...srcTag}
      {...srcSetTag}
    />
  )
})

const Picture = React.forwardRef((props, ref) => {
  const {
    className, imgClassName,
    src, x2, x3,
    fullWidth, fullHeight,
    alt, onError, itemProp,
    dataTestId, onlyImageTag,
    lazyLoad,
  } = props

  // Replace src and srcSet with data-src (if using fullpage.js lazy loading!!!)
  const srcTagName    = lazyLoad ? 'data-src' : 'src'
  const srcSetTagName = lazyLoad ? 'data-srcset' : 'srcSet'

  const srcTag = { [srcTagName]: src }

  if (lazyLoad) {
    srcTag.src = ''
  }

  // eslint-disable-next-line
  if (!props.srcSet && !x2 && !x3) {
    console.warn(`Why do you use Picture component if srcSet, x2, x3 not passed?! Check src: ${src}`)

    return (
      <img
        className={className}
        alt={alt}
        ref={ref}
        itemProp={itemProp}
        data-testid={dataTestId}
        {...srcTag}
      />
    )
  }

  const srcSet    = props.srcSet || getSrcSet(src, x2, x3) // eslint-disable-line
  const srcSetTag = { [srcSetTagName]: srcSet }

  const imgProps = { className, srcTag, src, srcSetTag, srcSet, fullWidth, fullHeight, alt, ref, onError, itemProp, dataTestId }

  if (onlyImageTag) {
    imgProps.className = cx(className, imgClassName)

    return (
      <NewPictureComponent {...imgProps} />
    )
  }

  imgProps.imgClassName = imgClassName

  return (
    <OldPictureComponent {...imgProps} />
  )
})

Picture.propTypes = {
  src: PropTypes.string.isRequired,
  srcSet: PropTypes.string,
  x2: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
  ]),
  x3: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
  ]),
  alt: PropTypes.string,
  className: PropTypes.string,
  imgClassName: PropTypes.string,
  fullWidth: PropTypes.bool,
  fullHeight: PropTypes.bool,
  dataTestId: PropTypes.string,
  onError: PropTypes.func,
  itemProp: PropTypes.string,
  onlyImageTag: PropTypes.bool, // set to remove <picture> and <source> tags
  lazyLoad: PropTypes.bool, // replace src and srcSet with data-src (when using fullpage.js lazy loading!!!)
}

Picture.defaultProps = {
  alt: '',
}


export default Picture
